import React, { PropsWithChildren, useMemo, useRef } from "react";

import { getDataTestId } from "../../../utils/getDataTestId";
import { WSIcon, WSIconProps } from "../core/WSIcon/WSIcon.component";
import { WSElementLayoutProps } from "../WSElement/WSElement.component";
import { WSTooltip, WSTooltipProps } from "../WSTooltip/WSTooltip";

const DEFAULT_ICON_SIZE = "S";
const DEFAULT_ICON_COLOR = "gray500";
const DEFAULT_ICON_NAME = "info-circle-fill";

export type WSTooltipIconProps = {
  tooltip?: string | Omit<PropsWithChildren<WSTooltipProps>, "target">;
  customAction?: () => void;
} & WSElementLayoutProps & {
    name?: WSIconProps["name"];
    size?: WSIconProps["size"];
    block?: WSIconProps["block"];
  };

export const WSTooltipIcon: React.FC<WSTooltipIconProps> = ({
  tooltip,
  customAction,
  ...iconProps
}) => {
  const tooltipIconRef = useRef<HTMLSpanElement>(null);

  const iconName: string =
    typeof iconProps.name === "string" ? iconProps.name : DEFAULT_ICON_NAME;
  const testIdBase = getDataTestId("tooltipIcon", iconName);

  const tooltipProps: WSTooltipProps = useMemo(
    () => ({
      position: "right",
      target: tooltipIconRef,
      name: testIdBase,
      ...(typeof tooltip === "string" ? { children: tooltip } : tooltip)
    }),
    [tooltip, testIdBase]
  );

  const tooltipText =
    typeof tooltip === "string"
      ? tooltip
      : typeof tooltip?.title === "string"
      ? tooltip.title
      : undefined;
  const ariaLabel = tooltipText ?? (tooltip ? "More info" : undefined);

  return (
    <>
      <WSIcon
        size={DEFAULT_ICON_SIZE}
        color={DEFAULT_ICON_COLOR}
        name={DEFAULT_ICON_NAME}
        onClick={customAction}
        ref={customAction ? undefined : tooltipIconRef}
        data-testid={`${testIdBase}Trigger`}
        role={customAction ? "button" : undefined}
        aria-label={ariaLabel}
        {...iconProps}
      />
      {customAction ? null : <WSTooltip {...tooltipProps} />}
    </>
  );
};
